import { jsxs, jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Icon } from '../Icon/icon.js';
import Spinner from '../Spinner/index.js';

const Button = ({
  ariaHasPopup,
  ariaLabel,
  buttonRef,
  children,
  className,
  isActive,
  isDisabled,
  isLink,
  isLoading,
  isSubmit,
  leftIcon,
  loadingText,
  onBlur,
  onClick,
  onMouseDown,
  rightIcon,
  size,
  style,
  testSection,
  title,
  whiteBackground,
  width = "default",
  ...props
}) => {
  const hasNoStyles = !style;
  const isOutlineButton = style ? ["outline", "outline-reverse"].includes(style) : false;
  const buttonClassNames = classNames(
    "oui-button",
    {
      [`oui-button--${size}`]: size,
      [`oui-button--${style}`]: style,
      [`oui-button--${width}`]: width,
      "flex--inline flex-justified--center flex-align--center": leftIcon || rightIcon,
      "is-active": isActive,
      "oui-button--loading": isLoading,
      "oui-button--white-background": whiteBackground === true && (hasNoStyles || isOutlineButton)
    },
    className
  );
  const type = isSubmit ? "submit" : "button";
  const handleOnClick = (event) => {
    if (isDisabled || isLoading || !onClick) {
      return;
    }
    onClick(event);
  };
  const leftIconClasses = classNames("flex", "flex-self--center", {
    "push--right": size !== "small",
    "push-half--right": size === "small"
  });
  const leftIconComp = leftIcon ? /* @__PURE__ */ jsx("div", { className: leftIconClasses, children: typeof leftIcon === "object" ? /* @__PURE__ */ jsx(Icon, { icon: leftIcon, size: size === "large" ? "medium" : "small" }) : /* @__PURE__ */ jsx(Icon, { name: leftIcon, size: size === "large" ? "medium" : "small" }) }) : "";
  const rightIconClasses = classNames("flex", "flex-self--center", {
    "push--left": size !== "small",
    "push-half--left": size === "small"
  });
  const rightIconComp = rightIcon ? /* @__PURE__ */ jsx("div", { className: rightIconClasses, children: typeof rightIcon === "object" ? /* @__PURE__ */ jsx(Icon, { icon: rightIcon, size: size === "large" ? "medium" : "small" }) : /* @__PURE__ */ jsx(Icon, { name: rightIcon, size: size === "large" ? "medium" : "small" }) }) : "";
  if (isLink) {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: buttonClassNames,
        "data-oui-component": true,
        "data-test-section": testSection,
        onBlur,
        ref: buttonRef,
        ...props,
        children: [
          leftIcon && leftIconComp,
          children,
          rightIcon && rightIconComp
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs(
    "button",
    {
      "aria-haspopup": ariaHasPopup,
      "aria-label": ariaLabel,
      "aria-live": "polite",
      className: buttonClassNames,
      "data-oui-component": true,
      "data-test-section": testSection,
      disabled: isDisabled || isLoading,
      onBlur,
      onClick: handleOnClick,
      onMouseDown,
      ref: buttonRef,
      title,
      type,
      ...props,
      children: [
        isLoading ? /* @__PURE__ */ jsx(Spinner, { "data-test-section": "button-spinner", size: "small" }) : leftIcon && leftIconComp,
        isLoading ? loadingText || "Processing" : children,
        !isLoading && rightIcon && rightIconComp
      ]
    }
  );
};
Button.propTypes = {
  /** Should be true if this button displays a menu or tooltip */
  ariaHasPopup: PropTypes.bool,
  /** Describes buttons that have an icon but no text */
  ariaLabel: PropTypes.string,
  /** React ref to the underlying button component */
  buttonRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.object })
  ]),
  /** Text within the button */
  children: PropTypes.node.isRequired,
  /** CSS class names. */
  className: PropTypes.string,
  /** Render button with active state */
  isActive: PropTypes.bool,
  /** Prevent users from interacting with the button */
  isDisabled: PropTypes.bool,
  /** Changes the button to a div for insertion within a Link component */
  isLink: PropTypes.bool,
  /** When true, adds a spinner to the button and disables the button */
  isLoading: PropTypes.bool,
  /** Make the button act as a submit button */
  isSubmit: PropTypes.bool,
  /** Icon to display on the left */
  leftIcon: PropTypes.node,
  /** When the button adds a spinner, it displays this text */
  loadingText: PropTypes.string,
  /** Function that fires when the button loses focus */
  onBlur: PropTypes.func,
  /** Function that fires when the button is clicked on */
  onClick: PropTypes.func,
  /** Function that fires when the button is mouse downed */
  onMouseDown: PropTypes.func,
  /** Icon to display on the right */
  rightIcon: PropTypes.node,
  /** Various height and width options */
  size: PropTypes.oneOf(["tiny", "small", "large", "narrow", "tight"]),
  /** Various color options */
  style: PropTypes.oneOf([
    "highlight",
    "danger",
    "danger-outline",
    "outline",
    "outline-reverse",
    "plain",
    "toggle",
    "underline",
    "unstyled"
  ]),
  /** Hook for automated JavaScript tests */
  testSection: PropTypes.string,
  /** Title of the button shown as tooltip text when the button is hovered */
  title: PropTypes.string,
  /** Show white background (only in Outline styled button) */
  whiteBackground: PropTypes.bool,
  /** Various height and width options */
  width: PropTypes.oneOf(["default", "full"])
};
Button.defaultProps = {
  isDisabled: false,
  isLink: false,
  isLoading: false,
  isSubmit: false,
  loadingText: "",
  onBlur: () => null,
  onClick: () => null,
  onMouseDown: () => null,
  width: "default"
};
Button.displayName = "Button";

export { Button as default };
